import { Component, inject, Input, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NgbActiveModal, NgbDropdownModule } from '@ng-bootstrap/ng-bootstrap';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { TranslocoDirective, TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { ToastrService } from 'ngx-toastr';
import { HttpEventType } from '@angular/common/http';
import { EmptyError, filter, lastValueFrom, Subject, tap } from 'rxjs';
import { FlatpickrDatePickerComponent } from '../../../../../../_shared/components/flatpickr-date-picker/flatpickr-date-picker.component';
import { InsuranceClaim, InsuranceClaimDocument, InsuranceClaimStatusLookup } from '../../../models/insurance-claim-response.model';
import { InsuranceClaimsService } from '../../../services/insurance-claims.service';
import { ErrorHandlerService } from '../../../../../../_shared/services/error-handler.service';
import { AuthService } from '../../../../../../_shared/services/auth.service';
import { CurrencyHelper } from '../../../../../../_shared/helpers/currency/currency-map.helper';
import { LookupService } from '../../../../../_shared/services/lookup.service';
import { LookupIncidentType } from '../../../../../_shared/models/lookup.model';
import { AccidentsAndIncidentsService } from '../../../../accidents-and-incidents/services/accidents-and-incidents.service';
import { IncidentAttachmentUpload } from '../../../../accidents-and-incidents/models/accidents-and-incidents-request.model';

interface ClaimAttachmentUpload extends IncidentAttachmentUpload {
  previewUrl: string | null;
  uploadCancel: Subject<void>;
}

@Component({
  selector: 'app-edit-insurance-claim-modal',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, FlatpickrDatePickerComponent, NgbDropdownModule, TranslocoDirective, TranslocoPipe],
  templateUrl: './edit-insurance-claim-modal.component.html',
  styleUrl: './edit-insurance-claim-modal.component.scss'
})
export class EditInsuranceClaimModalComponent implements OnInit, OnDestroy {
  private activeModal: NgbActiveModal = inject(NgbActiveModal);
  private fb: FormBuilder = inject(FormBuilder);
  private readonly transloco: TranslocoService = inject(TranslocoService);
  private claimsService: InsuranceClaimsService = inject(InsuranceClaimsService);
  private errorHandlerService: ErrorHandlerService = inject(ErrorHandlerService);
  private lookupService: LookupService = inject(LookupService);
  private accidentsAndIncidentsService: AccidentsAndIncidentsService = inject(AccidentsAndIncidentsService);
  private toastr: ToastrService = inject(ToastrService);
  private authService: AuthService = inject(AuthService);
  private cancelSubject: Subject<void> = new Subject<void>();

  currencySymbol: string = CurrencyHelper.getCurrencySymbol(this.authService.getUserSettings()?.currencyCode ?? 'ZAR');

  @Input() claim: InsuranceClaim | null = null;

  claimForm: FormGroup;

  categories: LookupIncidentType[] = [];
  selectedCategory: LookupIncidentType | null = null;
  isLoadingCategories: boolean = false;
  statuses: InsuranceClaimStatusLookup[] = [];
  selectedStatus: InsuranceClaimStatusLookup | null = null;
  dateFiled: string = '';
  isSubmitting: boolean = false;

  readonly maxAttachmentCount = 5;
  readonly maxVideoAttachmentSizeBytes = 150 * 1024 * 1024;
  newAttachments: ClaimAttachmentUpload[] = [];
  existingAttachments: InsuranceClaimDocument[] = [];
  attachmentIdsToDelete: number[] = [];
  isDragOver: boolean = false;
  attachmentError: string = '';

  constructor() {
    this.claimForm = this.fb.group({
      assetName: [{ value: '', disabled: true }, Validators.required],
      claimNumber: [{ value: '', disabled: true }, Validators.required],
      insuranceName: ['', Validators.required],
      settledAmount: ['']
    });
  }

  async ngOnInit(): Promise<void> {
    if (this.claim) {
      this.claimForm.patchValue({
        assetName: this.claim.assetName,
        claimNumber: this.claim.claimNumber,
        insuranceName: this.claim.insurance,
        settledAmount: this.claim.settledAmount
      });
      this.dateFiled = this.claim.dateFiled;
      this.existingAttachments = [...(this.claim.attachments ?? [])];
    }

    await Promise.all([this.loadCategories(), this.loadStatuses()]);
  }

  ngOnDestroy(): void {
    this.newAttachments.forEach(attachment => {
      this.cancelUpload(attachment);
      this.revokePreview(attachment);
    });
    this.cancelSubject.next();
    this.cancelSubject.complete();
  }

  get categoryLabel(): string {
    return this.selectedCategory
      ? (this.selectedCategory.localizedName || this.selectedCategory.name)
      : this.transloco.translate('accidents.modals.editClaim.selectCategory');
  }

  onCategorySelect(category: LookupIncidentType): void {
    this.selectedCategory = category;
  }

  get statusLabel(): string {
    return this.selectedStatus?.name || this.transloco.translate('accidents.modals.editClaim.selectStatus');
  }

  onStatusSelect(status: InsuranceClaimStatusLookup): void {
    this.selectedStatus = status;
  }

  get insuranceName(): string {
    return (this.claimForm.get('insuranceName')?.value ?? '').trim();
  }

  get settledAmount(): number | null | undefined {
    const value = this.claimForm.get('settledAmount')?.value;

    if (value === null || value === undefined || String(value).trim() === '') {
      return null;
    }

    const amount = Number(String(value).replace(/[^\d.-]/g, ''));
    return isNaN(amount) || amount < 0 ? undefined : amount;
  }

  get areAttachmentsReady(): boolean {
    return this.newAttachments.every(attachment => attachment.uploadSuccess && !attachment.isUploading && !attachment.uploadFailed);
  }

  get isFormValid(): boolean {
    return !!this.claim && !!this.selectedCategory && !!this.insuranceName && !!this.selectedStatus && this.settledAmount !== undefined && this.areAttachmentsReady;
  }

  isImage(mediaType: string | undefined, extension: string | null | undefined): boolean {
    if (mediaType) {
      return mediaType.startsWith('image/');
    }

    return ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.bmp'].includes(extension?.toLowerCase() ?? '');
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = !this.isSubmitting;
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;

    if (this.isSubmitting) {
      return;
    }

    const files = event.dataTransfer?.files;
    if (files && files.length > 0) {
      this.addFiles(files);
    }
  }

  openFilePicker(fileInput: HTMLInputElement): void {
    if (!this.isSubmitting) {
      fileInput.click();
    }
  }

  onFileSelect(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0 && !this.isSubmitting) {
      this.addFiles(input.files);
    }
    input.value = '';
  }

  downloadNewAttachment(attachment: ClaimAttachmentUpload): void {
    if (attachment.previewUrl) {
      this.triggerDownload(attachment.previewUrl, attachment.name, false);
      return;
    }

    const url = URL.createObjectURL(attachment.file);
    this.triggerDownload(url, attachment.name, false);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }

  downloadExistingAttachment(attachment: InsuranceClaimDocument): void {
    this.triggerDownload(attachment.url, attachment.name, true);
  }

  removeNewAttachment(attachment: ClaimAttachmentUpload): void {
    if (this.isSubmitting) {
      return;
    }

    this.cancelUpload(attachment);
    this.revokePreview(attachment);
    this.newAttachments = this.newAttachments.filter(item => item.reference !== attachment.reference);
    this.attachmentError = '';
  }

  retryUpload(attachment: ClaimAttachmentUpload): void {
    if (this.isSubmitting) {
      return;
    }

    this.attachmentError = '';
    attachment.uploadCancel = new Subject<void>();
    attachment.isUploading = true;
    attachment.uploadFailed = false;
    attachment.uploadSuccess = false;
    attachment.uploadProgress = 0;
    void this.uploadTempAttachment(attachment);
  }

  isMarkedForDeletion(attachment: InsuranceClaimDocument): boolean {
    return this.attachmentIdsToDelete.includes(attachment.documentId);
  }

  toggleExistingAttachmentDeletion(attachment: InsuranceClaimDocument): void {
    if (this.isSubmitting) {
      return;
    }

    this.attachmentError = '';

    if (!this.isMarkedForDeletion(attachment)) {
      this.attachmentIdsToDelete = [...this.attachmentIdsToDelete, attachment.documentId];
      return;
    }

    if (this.keptAttachmentCount >= this.maxAttachmentCount) {
      this.showMaxAttachmentsToast();
      return;
    }

    this.attachmentIdsToDelete = this.attachmentIdsToDelete.filter(id => id !== attachment.documentId);
  }

  get keptAttachmentCount(): number {
    return this.newAttachments.length + this.existingAttachments.length - this.attachmentIdsToDelete.length;
  }

  cancel(): void {
    if (this.isSubmitting) {
      return;
    }

    this.activeModal.dismiss();
  }

  async save(): Promise<void> {
    if (!this.isFormValid || this.isSubmitting) {
      return;
    }

    this.isSubmitting = true;

    try {
      await this.claimsService.updateInsuranceClaim({
        claimId: this.claim!.claimId,
        assetId: this.claim!.assetId,
        categoryId: this.selectedCategory!.id,
        insuranceName: this.insuranceName,
        settledAmount: this.settledAmount as number | null,
        statusId: this.selectedStatus!.id,
        attachments: this.newAttachments.map(({ name, reference, extension, mediaType }) => ({
          name,
          reference,
          extension,
          mediaType
        })),
        attachmentIdsToDelete: this.attachmentIdsToDelete
      }, this.cancelSubject);

      this.activeModal.close(true);
    } catch (error) {
      this.errorHandlerService.handleError(error, {
        showToast: true,
        callback: () => {
          this.isSubmitting = false;
        }
      });
    }
  }

  private async loadCategories(): Promise<void> {
    this.isLoadingCategories = true;

    try {
      const response = await this.lookupService.getIncidentTypesAsync(this.cancelSubject);
      this.categories = response.data ?? [];
      this.selectedCategory = this.categories.find(category =>
        category.id === this.claim?.categoryId || category.name === this.claim?.category
      ) ?? null;
      this.isLoadingCategories = false;
    } catch (error) {
      this.errorHandlerService.handleError(error, {
        callback: () => {
          this.categories = [];
          this.isLoadingCategories = false;
        }
      });
    }
  }

  private async loadStatuses(): Promise<void> {
    try {
      const response = await this.claimsService.getInsuranceClaimStatuses(this.cancelSubject);
      this.statuses = response.data ?? [];
      this.selectedStatus = this.statuses.find(status =>
        status.id === this.claim?.statusId || status.name === this.claim?.status
      ) ?? null;
    } catch (error) {
      this.errorHandlerService.handleError(error, {
        callback: () => {
          this.statuses = [];
        }
      });
    }
  }

  private addFiles(files: FileList): void {
    let hitLimit = false;
    this.attachmentError = '';

    for (let i = 0; i < files.length; i++) {
      const file = files[i];

      if (this.keptAttachmentCount >= this.maxAttachmentCount) {
        hitLimit = true;
        break;
      }

      if (!this.canAddFile(file)) {
        continue;
      }

      const attachment = this.createAttachmentUpload(file);
      this.newAttachments = [attachment, ...this.newAttachments];
      void this.uploadTempAttachment(attachment);
    }

    if (hitLimit) {
      this.showMaxAttachmentsToast();
    }
  }

  private showMaxAttachmentsToast(): void {
    this.toastr.warning(this.transloco.translate('accidents.modals.editClaim.errors.maxAttachments', { count: this.maxAttachmentCount }));
  }

  private canAddFile(file: File): boolean {
    if (!this.isSupportedFile(file)) {
      this.attachmentError = this.transloco.translate('accidents.modals.editClaim.errors.unsupportedFile');
      return false;
    }

    if (this.getMediaType(file).startsWith('video/') && file.size > this.maxVideoAttachmentSizeBytes) {
      this.attachmentError = this.transloco.translate('accidents.modals.reportIncident.errors.videoTooLarge');
      return false;
    }

    const exists = this.newAttachments.some(attachment => attachment.name === file.name && attachment.size === file.size);
    if (exists) {
      this.attachmentError = this.transloco.translate('accidents.modals.reportIncident.errors.duplicateFile');
      return false;
    }

    return true;
  }

  private createAttachmentUpload(file: File): ClaimAttachmentUpload {
    const extension = this.getFileExtension(file.name);
    const mediaType = this.getMediaType(file);

    return {
      file,
      size: file.size,
      name: file.name,
      reference: crypto.randomUUID(),
      extension,
      mediaType,
      previewUrl: this.isImage(mediaType, extension) ? URL.createObjectURL(file) : null,
      uploadCancel: new Subject<void>(),
      isUploading: true,
      uploadProgress: 0,
      uploadSuccess: false,
      uploadFailed: false
    };
  }

  private async uploadTempAttachment(attachment: ClaimAttachmentUpload): Promise<void> {
    if (!this.claim) {
      attachment.isUploading = false;
      attachment.uploadFailed = true;
      return;
    }

    try {
      const formData = new FormData();
      formData.append('AssetId', this.claim.assetId.toString());
      formData.append('Name', attachment.name);
      formData.append('Reference', attachment.reference);
      formData.append('Extension', attachment.extension ?? '');
      formData.append('MediaType', attachment.mediaType);
      formData.append('Attachment', attachment.file, attachment.name);

      await lastValueFrom(
        this.accidentsAndIncidentsService.uploadTempIncidentAttachment(formData, attachment.uploadCancel).pipe(
          tap(event => {
            if (event.type === HttpEventType.UploadProgress) {
              const total = event.total ?? attachment.size;
              const rawProgress = total > 0 ? Math.round((event.loaded / total) * 100) : 0;
              attachment.uploadProgress = Math.min(rawProgress, 95);
            }
          }),
          filter(event => event.type === HttpEventType.Response)
        )
      );

      attachment.uploadProgress = 100;
      attachment.uploadSuccess = true;
      attachment.uploadFailed = false;
      attachment.isUploading = false;
    } catch (error) {
      attachment.uploadFailed = true;
      attachment.uploadSuccess = false;

      // Upload was cancelled because the modal closed or the tile was removed, so there's nothing to report
      if (error instanceof EmptyError || !this.newAttachments.includes(attachment)) {
        return;
      }

      this.errorHandlerService.handleError(error, {
        showToast: true,
        callback: () => {
          this.attachmentError = this.transloco.translate('accidents.modals.reportIncident.errors.uploadFailed');
          attachment.isUploading = false;
        }
      });
    }
  }

  private triggerDownload(url: string, fileName: string, openInNewTab: boolean): void {
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    if (openInNewTab) {
      link.target = '_blank';
      link.rel = 'noopener';
    }
    link.click();
  }

  private cancelUpload(attachment: ClaimAttachmentUpload): void {
    attachment.uploadCancel.next();
    attachment.uploadCancel.complete();
  }

  private revokePreview(attachment: ClaimAttachmentUpload): void {
    if (attachment.previewUrl) {
      URL.revokeObjectURL(attachment.previewUrl);
    }
  }

  private isSupportedFile(file: File): boolean {
    const mediaType = this.getMediaType(file);
    return mediaType.startsWith('image/') || mediaType.startsWith('video/') || mediaType === 'application/pdf';
  }

  private getMediaType(file: File): string {
    return file.type || this.getMediaTypeFallback(this.getFileExtension(file.name));
  }

  private getFileExtension(fileName: string): string | null {
    const extension = fileName.includes('.') ? fileName.substring(fileName.lastIndexOf('.')) : '';
    return extension || null;
  }

  private getMediaTypeFallback(extension: string | null): string {
    const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.bmp', '.heic'];
    const videoExtensions = ['.mp4', '.mov', '.avi', '.mkv', '.webm'];
    const normalizedExtension = extension?.toLowerCase() ?? '';

    if (normalizedExtension === '.pdf') {
      return 'application/pdf';
    }

    if (imageExtensions.includes(normalizedExtension)) {
      return 'image/*';
    }

    if (videoExtensions.includes(normalizedExtension)) {
      return 'video/*';
    }

    return 'application/octet-stream';
  }
}